<template>
  <div class="nav-container" :class="{ 'is-mobile': isMobile }">
    <!-- 电脑端侧边导航 -->
    <div v-if="!isMobile" class="desktop-nav">
      <div class="nav-header">
        <DataCenterCard
          :theme="currentDataCenterTheme"
          title="数据中心"
          status-label="ONLINE"
          update-label="最后更新"
          update-date="20260806"
          @open-theme-drawer="openDataCenterThemeDrawer"
          @artwork-error="handleDataCenterArtworkError"
        />
      </div>

      <el-menu :default-active="activeIndex" class="custom-menu">
        <el-menu-item-group
          v-for="group in permissionMenuGroups"
          :key="group.key"
          class="menu-group"
        >
          <template #title>
            <span class="menu-group-title">{{ group.label }}</span>
          </template>

          <el-menu-item
            v-for="item in group.items"
            :key="item.index"
            :index="String(item.index)"
            :disabled="item.state === 'disabled'"
            :class="[`tone-${item.colorClass}`]"
            @click="handleMenuItemClick(item)"
          >
            <el-icon class="menu-item-icon">
              <component :is="iconComponents[item.iconKey]" />
            </el-icon>
            <span>{{ item.label }}</span>
          </el-menu-item>
        </el-menu-item-group>
      </el-menu>
    </div>

    <!-- 手机端1列布局 -->
    <div v-else class="mobile-nav">
      <!-- 顶部标题 -->
      <div class="mobile-header">
        <div class="mobile-update-meter">
          <span class="meter-label">VEHICLE DATA ONLINE</span>
          <div class="meter-main">
            <span class="car-glyph" aria-hidden="true">
              <svg viewBox="0 0 76 32" focusable="false">
                <path class="car-body" d="M7 21.5h5.7l6-6.4c2-2.1 4.2-3.1 7.2-3.1h18.9c3.2 0 5.8 1 8.2 3.1l6.9 6.1 7.2 1.2c2.4.4 4 2.1 4 4v1.1c0 .8-.7 1.5-1.5 1.5h-8.2a4.8 4.8 0 0 0-9.4 0H24.3a4.8 4.8 0 0 0-9.4 0H6.8c-1 0-1.8-.8-1.8-1.8V24c0-1.3.8-2.2 2-2.5Z" />
                <path class="car-window" d="M24.1 14.3h11.3v6.1H16.8l4.1-4.4c.9-1 1.9-1.7 3.2-1.7Zm14 0h6.5c2.1 0 3.7.6 5.2 1.9l4.8 4.2H38.1v-6.1Z" />
                <path class="car-light" d="M63.2 22.5h5.5c.8.2 1.4.8 1.7 1.5h-6.1c-.8 0-1.1-.7-1.1-1.5Z" />
                <path class="car-emblem" d="M32.6 22.3 36 19l3.4 3.3-3.4 3.3-3.4-3.3Z" />
                <circle class="car-wheel" cx="19.6" cy="28.7" r="3.3" />
                <circle class="car-wheel" cx="56.7" cy="28.7" r="3.3" />
              </svg>
            </span>
            <span class="meter-copy">
              <span class="meter-text">站点最近更新</span>
              <strong>20260806</strong>
            </span>
          </div>
        </div>
      </div>

      <!-- 1列菜单列表 -->
      <div class="menu-list">
        <section
          v-for="group in permissionMenuGroups"
          :key="group.key"
          class="mobile-menu-group"
        >
          <h2 class="mobile-menu-group-title">{{ group.label }}</h2>

          <div
            v-for="item in group.items"
            :key="item.index"
            class="list-item"
            :class="{
              disabled: item.state === 'disabled',
              'is-active': String(item.index) === activeIndex,
            }"
            :aria-current="String(item.index) === activeIndex ? 'page' : undefined"
            @click="handleMobileMenuItemClick(item)"
          >
            <div class="item-left">
              <div class="item-icon" :class="item.colorClass">
                <el-icon :size="20">
                  <component :is="iconComponents[item.iconKey]" />
                </el-icon>
              </div>
              <span class="item-label">{{ item.label }}</span>
            </div>
            <el-icon class="item-arrow">
              <ArrowRight />
            </el-icon>
          </div>
        </section>
      </div>
    </div>

    <DataCenterThemeDrawer
      v-model:open="isDataCenterThemeDrawerOpen"
      :themes="dataCenterThemes"
      :selected-theme-id="currentDataCenterThemeId"
      @select="selectDataCenterTheme"
    />
  </div>
</template>

<script>
// 导入 Element Plus 的图标组件  
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { ElMessage } from "element-plus";
import { buildNavigationGroups } from '@/config/navMenu.mjs';
import DataCenterCard from '@/components/DataCenterCard.vue';
import DataCenterThemeDrawer from '@/components/DataCenterThemeDrawer.vue';
import {
  DEFAULT_DATA_CENTER_THEME_ID,
  dataCenterThemes,
  resolveDataCenterTheme,
} from '@/config/dataCenterThemes.mjs';
import {
  readDataCenterThemeId,
  writeDataCenterThemeId,
} from '@/utils/dataCenterThemeStorage.mjs';
import {
  Key,
  Avatar,
  Tickets,
  Lock,
  Star,
  Connection,
  CollectionTag,
  Download,
  Suitcase,
  ArrowRight,
  Operation,
  Setting,
} from "@element-plus/icons-vue";

const iconComponents = {
  key: Key,
  avatar: Avatar,
  tickets: Tickets,
  lock: Lock,
  star: Star,
  connection: Connection,
  collectionTag: CollectionTag,
  download: Download,
  suitcase: Suitcase,
  operation: Operation,
  setting: Setting,
};

export default {
  name: "NavBar",
  components: {
    DataCenterCard,
    DataCenterThemeDrawer,
    Key,
    Avatar,
    Tickets,
    Lock,
    Star,
    Connection,
    CollectionTag,
    Download,
    Suitcase,
    ArrowRight,
    Operation,
    Setting,
  },
  emits: ["nav-click"],
  setup(props, { emit }) {
    const route = useRoute();
    const store = useStore();
    
    const currentUsername = computed(
      () => String(store.state.userData?.username || '').trim(),
    );
    const currentDataCenterThemeId = ref(DEFAULT_DATA_CENTER_THEME_ID);
    const currentDataCenterTheme = computed(
      () => resolveDataCenterTheme(currentDataCenterThemeId.value),
    );
    const isDataCenterThemeDrawerOpen = ref(false);

    function openDataCenterThemeDrawer() {
      isDataCenterThemeDrawerOpen.value = true;
    }

    watch(
      currentUsername,
      () => {
        currentDataCenterThemeId.value = readDataCenterThemeId(currentUsername.value);
      },
      { immediate: true },
    );
    
    function selectDataCenterTheme(themeId) {
      const resolved = resolveDataCenterTheme(themeId);
      currentDataCenterThemeId.value = resolved.id;
      writeDataCenterThemeId(currentUsername.value, resolved.id);
    }
    
    function handleDataCenterArtworkError() {
      if (currentDataCenterThemeId.value === DEFAULT_DATA_CENTER_THEME_ID) return;
      currentDataCenterThemeId.value = DEFAULT_DATA_CENTER_THEME_ID;
      writeDataCenterThemeId(currentUsername.value, DEFAULT_DATA_CENTER_THEME_ID);
    }
    
    // 响应式判断手机端
    const isMobile = ref(window.innerWidth <= 768);
    const showDataSwitchMenu = false;

    const handleResize = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });

    const permissionMenuGroups = computed(() => buildNavigationGroups(
      store.state.userData,
      { showDataSwitchMenu },
    ));

    const permissionMenuItems = computed(() => permissionMenuGroups.value.flatMap(
      (group) => group.items,
    ));

    const activeIndex = computed(() => {
      const matched = permissionMenuItems.value.find(item => item.name === route.name);
      return matched ? String(matched.index) : '1';
    });

    const handleMenuItemClick = (item) => {
      if (item.state === 'disabled') {
        ElMessage.warning('当前账号无此菜单权限');
        return;
      }
      emit("nav-click", { name: item.name });
    };

    // 移动端菜单点击
    const handleMobileMenuItemClick = (item) => {
      handleMenuItemClick(item);
    };

    return {
      isMobile,
      iconComponents,
      permissionMenuGroups,
      permissionMenuItems,
      activeIndex,
      handleMenuItemClick,
      handleMobileMenuItemClick,
      dataCenterThemes,
      currentDataCenterThemeId,
      currentDataCenterTheme,
      isDataCenterThemeDrawerOpen,
      openDataCenterThemeDrawer,
      selectDataCenterTheme,
      handleDataCenterArtworkError,
    };
  },
};
</script>

<style scoped>
/* ========== 通用样式 ========== */
.nav-container {
  height: 100%;
  background: transparent;
}

/* ========== 电脑端样式 ========== */
.desktop-nav {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 12px 10px;
  background:
    linear-gradient(180deg, rgba(248, 250, 252, 0.96) 0%, rgba(255, 255, 255, 0.98) 100%);
  border-right: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.9);
}

.nav-header {
  padding: 0 0 12px;
}

.update-meter {
  position: relative;
  overflow: hidden;
  padding: 12px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background:
    linear-gradient(135deg, rgba(239, 246, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 56%, rgba(240, 253, 250, 0.86) 100%);
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
}

.update-meter::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(37, 99, 235, 0.08) 1px, transparent 1px),
    linear-gradient(180deg, rgba(15, 23, 42, 0.05) 1px, transparent 1px);
  background-size: 18px 18px;
  opacity: 0.5;
  pointer-events: none;
}

.meter-top,
.meter-main {
  position: relative;
  z-index: 1;
}

.meter-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.meter-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.08em;
}

.meter-state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #047857;
  font-size: 11px;
  font-weight: 700;
}

.meter-state::before {
  content: "";
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.13);
}

.meter-main {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
}

.car-glyph {
  width: 34px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0f172a;
  flex: 0 0 auto;
  filter: drop-shadow(0 5px 8px rgba(15, 23, 42, 0.18));
}

.car-glyph svg {
  width: 29px;
  height: 16px;
  overflow: visible;
}

.car-body,
.car-wheel {
  fill: currentColor;
}

.car-window {
  fill: rgba(148, 232, 255, 0.72);
}

.car-light,
.car-emblem {
  fill: #22d3ee;
}

.car-emblem {
  filter: drop-shadow(0 0 4px rgba(34, 211, 238, 0.8));
}

.meter-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.meter-text {
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.meter-main strong {
  color: #0f172a;
  font-size: 15px;
  line-height: 1.1;
  font-family: "Consolas", "Monaco", monospace;
  white-space: nowrap;
  letter-spacing: 0;
}

.meter-track {
  position: relative;
  z-index: 1;
  height: 4px;
  margin-top: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: #e2e8f0;
}

.meter-track span {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 42%;
  border-radius: inherit;
  background: linear-gradient(90deg, transparent 0%, #2563eb 16%, #06b6d4 56%, #10b981 100%);
  animation: dataSweep 7s ease-in-out infinite;
}

@keyframes dataSweep {
  0% {
    transform: translateX(-120%);
  }

  78% {
    transform: translateX(260%);
  }

  100% {
    transform: translateX(260%);
  }
}

/* 自定义菜单样式 */
.custom-menu {
  border-right: none !important;
  flex: 1;
  background: transparent;
  padding: 0 0 12px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.custom-menu :deep(.el-menu-item-group) {
  margin: 0;
  padding: 0 0 1px;
}

.custom-menu :deep(.el-menu-item-group + .el-menu-item-group) {
  margin-top: 2px;
  padding-top: 2px;
}

.custom-menu :deep(.el-menu-item-group__title) {
  padding: 4px 9px 2px;
  line-height: 16px;
}

.menu-group-title {
  color: #8192aa;
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.02em;
}

.custom-menu :deep(.el-menu-item) {
  height: 38px;
  margin: 1px 0;
  padding: 0 9px !important;
  border: 0;
  border-radius: 11px;
  color: #425466;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.18s ease, background-color 0.18s ease;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.custom-menu :deep(.el-menu-item:hover) {
  color: #25476d;
  background: #f3f7fc;
}

.custom-menu :deep(.el-menu-item.is-active) {
  color: #0f4fa8;
  background: linear-gradient(90deg, #e7f1ff 0%, #eef6ff 100%);
  font-weight: 700;
  box-shadow: none;
}

.custom-menu :deep(.el-menu-item .menu-item-icon) {
  width: 30px;
  height: 30px;
  margin-right: 10px;
  flex: 0 0 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #edf4ff;
  color: #2f7cf6;
  font-size: 18px;
  transition: color 0.18s ease, background-color 0.18s ease;
}

.custom-menu :deep(.el-menu-item.tone-teal .menu-item-icon) {
  color: #32ad9f;
  background: #eaf8f5;
}

.custom-menu :deep(.el-menu-item.tone-indigo .menu-item-icon) {
  color: #7a64f4;
  background: #f1edff;
}

.custom-menu :deep(.el-menu-item.tone-sky .menu-item-icon) {
  color: #4f9ff7;
  background: #eaf5ff;
}

.custom-menu :deep(.el-menu-item.tone-coral .menu-item-icon) {
  color: #f26f65;
  background: #fff0ed;
}

.custom-menu :deep(.el-menu-item.tone-purple .menu-item-icon) {
  color: #8067f3;
  background: #f2eeff;
}

.custom-menu :deep(.el-menu-item.tone-cyan .menu-item-icon) {
  color: #20a9c7;
  background: #e9f9fc;
}

.custom-menu :deep(.el-menu-item.is-active .menu-item-icon) {
  color: #176fe8;
  background: #dcecff;
}

.custom-menu :deep(.el-menu-item span) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1;
}

.custom-menu :deep(.el-menu-item.is-disabled) {
  opacity: 0.48;
  cursor: not-allowed;
}

/* ========== 手机端1列样式 ========== */
.mobile-nav {
  min-height: 100vh;
  background: linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%);
  padding-bottom: 20px;
}

/* 顶部标题 */
.mobile-header {
  padding: 24px 20px;
  margin-bottom: 16px;
  border-bottom-left-radius: 30px;
  border-bottom-right-radius: 30px;
  background:
    linear-gradient(135deg, #0f172a 0%, #1e293b 54%, #0f766e 100%);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.22);
}

.mobile-update-meter {
  color: #ffffff;
}

.mobile-update-meter .meter-label {
  color: rgba(255, 255, 255, 0.62);
}

.mobile-update-meter .meter-main {
  margin-top: 10px;
}

.mobile-update-meter .car-glyph {
  background: rgba(255, 255, 255, 0.14);
  box-shadow: none;
}

.mobile-update-meter .meter-text {
  color: rgba(255, 255, 255, 0.78);
}

.mobile-update-meter .meter-main strong {
  color: #ffffff;
}

/* 1列菜单列表 */
.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0 8px 20px;
}

.mobile-menu-group {
  padding: 4px 0 10px;
}

.mobile-menu-group + .mobile-menu-group {
  padding-top: 12px;
  border-top: 1px solid #eef2f6;
}

.mobile-menu-group-title {
  margin: 0;
  padding: 0 8px 7px;
  color: #8192aa;
  font-size: 12px;
  font-weight: 650;
  line-height: 18px;
  letter-spacing: 0.02em;
}

.list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 46px;
  margin: 3px 0;
  padding: 6px;
  background: transparent;
  border-radius: 12px;
  box-shadow: none;
  transition: color 0.18s ease, background-color 0.18s ease;
  cursor: pointer;
  border: 0;
}

.list-item:active {
  background-color: #f3f7fc;
}

.list-item.is-active {
  background: linear-gradient(90deg, #e7f1ff 0%, #eef6ff 100%);
}

.list-item.disabled {
  opacity: 0.52;
  cursor: not-allowed;
}

.list-item.disabled:active {
  transform: none;
}

.item-left {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
}

.item-icon {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.2s ease;
}

/* 图标背景颜色 */
.item-icon.blue {
  background-color: #edf4ff;
  color: #2f7cf6;
}

.item-icon.orange {
  background-color: #fdf6ec;
  color: #e6a23c;
}

.item-icon.green {
  background-color: #f0f9eb;
  color: #67c23a;
}

.item-icon.teal {
  background-color: #eaf8f5;
  color: #32ad9f;
}

.item-icon.red {
  background-color: #fef0f0;
  color: #f56c6c;
}

.item-icon.coral {
  background-color: #fff0ed;
  color: #f26f65;
}

.item-icon.purple {
  background-color: #f2eeff;
  color: #8067f3;
}

.item-icon.cyan {
  background-color: #e9f9fc;
  color: #20a9c7;
}

.item-icon.sky {
  background-color: #eaf5ff;
  color: #4f9ff7;
}

.item-icon.pink {
  background-color: #fff0f6;
  color: #eb2f96;
}

.item-icon.indigo {
  background-color: #f1edff;
  color: #7a64f4;
}

.item-icon.brown {
  background-color: #f7f0e6;
  color: #b85c5c;
}

.item-icon .el-icon {
  font-size: 20px;
}

.item-label {
  overflow: hidden;
  color: #425466;
  font-size: 14px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.list-item.is-active .item-icon {
  color: #176fe8;
  background: #dcecff;
}

.list-item.is-active .item-label {
  color: #0f4fa8;
  font-weight: 700;
}

.item-arrow {
  flex: 0 0 auto;
  margin-left: 4px;
  color: #909399;
  font-size: 14px;
  transition: transform 0.2s ease;
}

.list-item:active .item-arrow {
  transform: translateX(3px);
  color: #409eff;
}

/* 响应式调整 */
@media screen and (max-width: 768px) {
  .desktop-nav {
    display: none;
  }
}

@media screen and (min-width: 769px) {
  .mobile-nav {
    display: none;
  }
}

/* 保留菜单滚动能力，但不显示滚动条 */
.custom-menu::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

:global(html.dark .desktop-nav) {
  border-right-color: var(--wf-border);
  background: linear-gradient(180deg, rgba(17, 24, 39, 0.98) 0%, rgba(11, 18, 32, 0.99) 100%);
  box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.03);
}

:global(html.dark .update-meter) {
  border-color: rgba(59, 130, 246, 0.28);
  background: linear-gradient(135deg, #17243a 0%, #111827 56%, #122b2c 100%);
  box-shadow: 0 12px 26px var(--wf-shadow);
}

:global(html.dark .update-meter::before) {
  background:
    linear-gradient(90deg, rgba(96, 165, 250, 0.08) 1px, transparent 1px),
    linear-gradient(180deg, rgba(226, 232, 240, 0.05) 1px, transparent 1px);
}

:global(html.dark .meter-label),
:global(html.dark .menu-group-title),
:global(html.dark .mobile-menu-group-title) {
  color: var(--wf-text-muted);
}

:global(html.dark .meter-state) {
  color: #6ee7b7;
}

:global(html.dark .car-glyph),
:global(html.dark .meter-main strong) {
  color: var(--wf-text-primary);
}

:global(html.dark .meter-text),
:global(html.dark .custom-menu .el-menu-item),
:global(html.dark .item-label) {
  color: var(--wf-text-regular);
}

:global(html.dark .meter-track) {
  background: #334155;
}

:global(html.dark .custom-menu .el-menu-item:hover),
:global(html.dark .list-item:active) {
  color: var(--wf-text-primary);
  background: var(--wf-hover-bg);
}

:global(html.dark .custom-menu .el-menu-item.is-active),
:global(html.dark .list-item.is-active) {
  color: #bfdbfe;
  background: linear-gradient(90deg, var(--wf-active-bg-start) 0%, var(--wf-active-bg-end) 100%);
}

:global(html.dark .custom-menu .el-menu-item .menu-item-icon),
:global(html.dark .item-icon) {
  background: #1c2b43;
}

:global(html.dark .custom-menu .el-menu-item.tone-teal .menu-item-icon),
:global(html.dark .item-icon.teal) {
  background: rgba(45, 212, 191, 0.13);
}

:global(html.dark .custom-menu .el-menu-item.tone-indigo .menu-item-icon),
:global(html.dark .item-icon.indigo),
:global(html.dark .custom-menu .el-menu-item.tone-purple .menu-item-icon),
:global(html.dark .item-icon.purple) {
  background: rgba(139, 92, 246, 0.15);
}

:global(html.dark .custom-menu .el-menu-item.tone-sky .menu-item-icon),
:global(html.dark .item-icon.sky),
:global(html.dark .custom-menu .el-menu-item.tone-cyan .menu-item-icon),
:global(html.dark .item-icon.cyan) {
  background: rgba(56, 189, 248, 0.13);
}

:global(html.dark .custom-menu .el-menu-item.tone-coral .menu-item-icon),
:global(html.dark .item-icon.coral),
:global(html.dark .item-icon.red),
:global(html.dark .item-icon.orange) {
  background: rgba(248, 113, 113, 0.13);
}

:global(html.dark .custom-menu .el-menu-item.is-active .menu-item-icon),
:global(html.dark .list-item.is-active .item-icon) {
  color: #93c5fd;
  background: rgba(59, 130, 246, 0.2);
}

:global(html.dark .list-item.is-active .item-label) {
  color: #bfdbfe;
}

:global(html.dark .mobile-nav) {
  background: linear-gradient(180deg, var(--wf-surface) 0%, var(--wf-page-bg) 100%);
}

:global(html.dark .mobile-menu-group + .mobile-menu-group) {
  border-top-color: var(--wf-border);
}
</style>
